import { useState, useEffect } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LOGO_SRC } from '../../components/layout/Header';
import {
  Users, LogOut, Menu, X, ClipboardList, Upload, Grid3X3, MessageSquare, Home
} from 'lucide-react';

const NAV_ITEMS = [
  { name: 'Kunden', href: '/admin', icon: Users },
  { name: 'Erstberatungen', href: '/admin/consultations', icon: ClipboardList },
  { name: 'Kontaktanfragen', href: '/admin/contacts', icon: MessageSquare },
  { name: 'Import/Export', href: '/admin/import', icon: Upload },
  { name: 'Kategorien', href: '/admin/categories', icon: Grid3X3 },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (href) => (href === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(href));

  return (
    <div className="min-h-screen bg-off" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 bg-ink text-off border-b-2 border-lilac px-4 py-3 flex items-center justify-between">
        <span className="font-display font-bold flex items-center gap-2">
          <span className="text-lilac" aria-hidden="true">✳</span> Admin Panel
        </span>
        <button
          type="button"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="inline-flex items-center justify-center w-10 h-10 border-2 border-off"
          aria-label={sidebarOpen ? 'Menü schließen' : 'Menü öffnen'}
          data-testid="admin-mobile-menu-btn"
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-64 pt-16 lg:pt-0 bg-ink text-off border-r-2 border-lilac flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="admin-sidebar"
      >
        <div className="p-5 border-b-2 border-off/20">
          <Link to="/" className="inline-block bg-off border-2 border-lilac p-2.5 pr-3" aria-label="Zur Website">
            <img src={LOGO_SRC} alt="gastro-beratung.de" width="600" height="108" className="h-7 w-auto" />
          </Link>
          <p className="label-caps text-lilac mt-4">Admin Panel</p>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto" aria-label="Admin-Navigation">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 border-l-4 transition-colors font-display font-bold ${
                isActive(item.href)
                  ? 'bg-purple text-off border-lilac'
                  : 'text-off/80 border-transparent hover:bg-off/10 hover:text-off'
              }`}
              data-testid={`admin-nav-${item.href.replace('/admin', '').replace('/', '') || 'customers'}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t-2 border-off/20">
          <div className="mb-4 px-1">
            <p className="text-sm font-bold text-off">{user?.name}</p>
            <p className="text-xs text-lilac">Administrator</p>
          </div>
          <div className="flex flex-col gap-2">
            <Link to="/" className="btn btn--ghost-off btn--xs justify-start" data-testid="admin-to-website">
              <Home className="w-4 h-4" />
              Zur Website
            </Link>
            <button type="button" onClick={handleLogout} className="btn btn--lilac btn--xs justify-start" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-ink/70 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
